import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../lang/useLanguage";
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const money = (v) => v != null ? `${Number(v).toLocaleString("tr-TR")} ₺` : "—";

const thresholdStatus = (value, warning, critical) => {
  if (value >= critical) return "critical";
  if (value >= warning)  return "warning";
  return "ok";
};

const statusColor = (status, t) => ({
  critical: t.accent.red,
  warning:  t.accent.yellow,
  ok:       t.accent.green,
})[status] || t.accent.green;

// ─── METRİK KARTI ─────────────────────────────────────────────────────────
const MetricCard = ({ label, value, unit, icon, color, sub, t }) => (
  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: color || t.accent.blue, fontVariantNumeric: "tabular-nums" }}>
          {value ?? "—"}
          <span style={{ fontSize: 13, fontWeight: 500, color: t.text.muted, marginLeft: 4 }}>{unit}</span>
        </div>
        {sub && <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      <span style={{ fontSize: 26, opacity: 0.7 }}>{icon}</span>
    </div>
  </div>
);

// ─── EŞİK GÖSTERGESİ ──────────────────────────────────────────────────────
const ThresholdGauge = ({ label, value, unit, warning, critical, t }) => {
  const status   = thresholdStatus(value, warning, critical);
  const color    = statusColor(status, t);
  const max      = critical * 1.2;
  const pct      = Math.min(100, (value / max) * 100);
  const warnPct  = (warning  / max) * 100;
  const critPct  = (critical / max) * 100;
  return (
    <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: t.text.primary }}>{label}</span>
        <span style={{ fontSize: 15, fontWeight: 800, color }}>{value} {unit}</span>
      </div>
      <div style={{ position: "relative", height: 10, background: t.bg.tag, borderRadius: 5, overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, ${t.accent.green} ${warnPct}%, ${t.accent.yellow} ${warnPct}% ${critPct}%, ${t.accent.red} ${critPct}%)`, opacity: 0.25 }} />
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, background: color, borderRadius: 5, transition: "width 0.6s ease" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: t.text.muted }}>
        <span>0</span>
        <span style={{ color: t.accent.yellow }}>⚠ {warning}</span>
        <span style={{ color: t.accent.red }}>🔴 {critical}</span>
      </div>
    </div>
  );
};

// ─── EŞİK AYARLARI MODAL ─────────────────────────────────────────────────
const ThresholdSettingsModal = ({ settings, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const e = l.energyPage || {};
  const [form, setForm] = useState({
    kwh_warning:   settings?.kwh_warning   || 800,
    kwh_critical:  settings?.kwh_critical  || 1000,
    cost_warning:  settings?.cost_warning  || 5000,
    cost_critical: settings?.cost_critical || 8000,
    co2_warning:   settings?.co2_warning   || 400,
    co2_critical:  settings?.co2_critical  || 600,
    peak_warning:  settings?.peak_warning  || 150,
    peak_critical: settings?.peak_critical || 200,
    tariff_day:    settings?.tariff_day    || 2.5,
    tariff_night:  settings?.tariff_night  || 1.8,
    tariff_peak:   settings?.tariff_peak   || 3.2,
    alert_email:   settings?.alert_email   || "",
    alert_sms:     settings?.alert_sms     || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/energy/settings`, { method: "POST", headers: headers(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || (e.errGeneric || "Hata"));
      onSaved(form);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em", paddingBottom: 8, borderBottom: `1px solid ${t.border.subtle}` }}>{title}</div>
      {children}
    </div>
  );

  const Row = ({ label, k, unit, type = "number" }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <label style={{ flex: 1, fontSize: 13, color: t.text.secondary }}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 6, width: 160 }}>
        <input type={type} value={form[k]} onChange={ev => set(k, ev.target.value)} style={{ ...inputStyle, width: "100%" }} />
        {unit && <span style={{ fontSize: 12, color: t.text.muted, flexShrink: 0 }}>{unit}</span>}
      </div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={ev => ev.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>⚙️ {e.settingsTitle || "Enerji Ayarları"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <Section title={`⚡ ${e.sectionKwh   || "Tüketim Eşikleri (kWh/gün)"}`}>
            <Row label={e.warningThreshold  || "Uyarı Eşiği"}  k="kwh_warning"  unit="kWh" />
            <Row label={e.criticalThreshold || "Kritik Eşik"} k="kwh_critical" unit="kWh" />
          </Section>
          <Section title={`💰 ${e.sectionCost  || "Maliyet Eşikleri (₺/gün)"}`}>
            <Row label={e.warningThreshold  || "Uyarı Eşiği"}  k="cost_warning"  unit="₺" />
            <Row label={e.criticalThreshold || "Kritik Eşik"} k="cost_critical" unit="₺" />
          </Section>
          <Section title={`🌿 ${e.sectionCo2  || "CO₂ Eşikleri (kg/gün)"}`}>
            <Row label={e.warningThreshold  || "Uyarı Eşiği"}  k="co2_warning"  unit="kg" />
            <Row label={e.criticalThreshold || "Kritik Eşik"} k="co2_critical" unit="kg" />
          </Section>
          <Section title={`📈 ${e.sectionPeak || "Güç Eşikleri (kW)"}`}>
            <Row label={e.warningThreshold  || "Uyarı Eşiği"}  k="peak_warning"  unit="kW" />
            <Row label={e.criticalThreshold || "Kritik Eşik"} k="peak_critical" unit="kW" />
          </Section>
          <Section title={`💡 ${e.sectionTariff || "Elektrik Tarifeleri (₺/kWh)"}`}>
            <Row label={e.tariffDay   || "Gündüz Tarifesi"}  k="tariff_day"   unit="₺" />
            <Row label={e.tariffNight || "Gece Tarifesi"}    k="tariff_night" unit="₺" />
            <Row label={e.tariffPeak  || "Puant Tarifesi"}   k="tariff_peak"  unit="₺" />
          </Section>
          <Section title={`🔔 ${e.sectionNotification || "Bildirim Ayarları"}`}>
            <Row label={e.email_     || "E-posta"}          k="alert_email" unit="" type="email" />
            <Row label={e.smsTelegram|| "SMS / Telegram"}   k="alert_sms"   unit="" type="text"  />
          </Section>
          {error && <div style={{ padding: "10px 16px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>
            {e.cancel || "İptal"}
          </button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600, boxShadow: loading ? "none" : t.shadow.btn }}>
            {loading ? (e.saving || "Kaydediliyor...") : (e.save || "Kaydet")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MAKİNE ENERJİ DETAY MODAL ────────────────────────────────────────────
const MachineEnergyModal = ({ machine, onClose, t }) => {
  const { t: l } = useLanguage();
  const e = l.energyPage || {};
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/energy/machine/${machine.machine_id}?days=7`, { headers: headers() });
        if (res.ok) setHistory(await res.json());
      } catch {}
    };
    fetch_();
  }, [machine.machine_id]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 680, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={ev => ev.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>⚡ {machine.machine_name || machine.machine_id}</div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>{e.sevenDayHistory || "7 günlük enerji geçmişi"}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
            {[
              { label: e.currentPower   || "Anlık Güç",        value: machine.current_power?.toFixed(1), unit: "kW",  color: t.accent.blue },
              { label: e.todayConsump   || "Bugünkü Tüketim",  value: machine.today_kwh?.toFixed(1),    unit: "kWh", color: t.accent.green },
              { label: e.todayCost      || "Bugünkü Maliyet",  value: machine.today_cost?.toFixed(0),   unit: "₺",   color: t.accent.purple },
            ].map(({ label, value, unit, color }) => (
              <div key={label} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 6, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color }}>
                  {value ?? "—"}<span style={{ fontSize: 12, color: t.text.muted, marginLeft: 3 }}>{unit}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>
              {e.sevenDayConsumption || "7 Günlük Tüketim"}
            </div>
            {history.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                  <XAxis dataKey="date" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} tickFormatter={v => v?.slice(5)} />
                  <YAxis stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                  <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                  <Bar dataKey="kwh" fill={t.accent.blue} radius={[4, 4, 0, 0]} name="kWh" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: "center", color: t.text.placeholder, padding: "40px 0" }}>
                {e.noHistoryData || "Geçmiş veri bulunamadı"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function Energy() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const e = l.energyPage || {};

  const [mainTab,        setMainTab]        = useState("overview");
  const [summary,        setSummary]        = useState(null);
  const [machines,       setMachines]       = useState([]);
  const [hourlyData,     setHourlyData]     = useState([]);
  const [dailyData,      setDailyData]      = useState([]);
  const [monthlyData,    setMonthlyData]    = useState([]);
  const [settings,       setSettings]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [,               setError]          = useState("");
  const [showSettings,   setShowSettings]   = useState(false);
  const [selectedMachine,setSelectedMachine]= useState(null);
  const [period,         setPeriod]         = useState("daily");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5] = await Promise.all([
        fetch(`${API}/energy/summary`,      { headers: headers() }),
        fetch(`${API}/energy/machines`,     { headers: headers() }),
        fetch(`${API}/energy/hourly`,       { headers: headers() }),
        fetch(`${API}/energy/daily?days=30`,{ headers: headers() }),
        fetch(`${API}/energy/settings`,     { headers: headers() }),
      ]);
      if (r1.ok) setSummary(await r1.json());
      if (r2.ok) { const d = await r2.json(); setMachines(Array.isArray(d) ? d : d.items || []); }
      if (r3.ok) setHourlyData(await r3.json());
      if (r4.ok) setDailyData(await r4.json());
      if (r5.ok) { const d = await r5.json(); setSettings(d.settings || d); }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const fetchMonthly = async () => {
      try {
        const res = await fetch(`${API}/energy/monthly`, { headers: headers() });
        if (res.ok) setMonthlyData(await res.json());
      } catch {}
    };
    fetchMonthly();
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartData   = period === "hourly" ? hourlyData : period === "daily" ? dailyData : monthlyData;
  const xKey        = period === "hourly" ? "hour"     : period === "daily" ? "date"    : "month";
  const xFormatter  = period === "hourly" ? v => `${v}:00` : period === "daily" ? v => v?.slice(5) : v => v;

  const periodLabel = period === "hourly"
    ? (e.periodHourly  || "Saatlik")
    : period === "daily"
    ? (e.periodDaily   || "Günlük")
    : (e.periodMonthly || "Aylık");

  const thresholds    = settings || { kwh_warning: 800, kwh_critical: 1000, cost_warning: 5000, cost_critical: 8000 };
  const todayKwh      = summary?.today_kwh  || 0;
  const todayCost     = summary?.today_cost || 0;
  const energyStatus  = thresholdStatus(todayKwh,   thresholds.kwh_warning,  thresholds.kwh_critical);
  const costStatus    = thresholdStatus(todayCost,   thresholds.cost_warning, thresholds.cost_critical);

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); @keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>
              ⚡ {e.title || "Enerji Takibi"}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {e.subtitle || "Gerçek zamanlı tüketim · Maliyet analizi · CO₂ takibi · Eşik ayarları"}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>
              🔄 {e.refresh || "Yenile"}
            </button>
            <button onClick={() => setShowSettings(true)} style={{ padding: "10px 20px", borderRadius: 10, border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg, color: t.accent.blue, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              ⚙️ {e.settings || "Ayarlar"}
            </button>
          </div>
        </div>

        {/* Durum uyarısı */}
        {(energyStatus !== "ok" || costStatus !== "ok") && (
          <div style={{ padding: "12px 20px", marginBottom: 20, borderRadius: 12, background: energyStatus === "critical" || costStatus === "critical" ? t.accent.redBg : t.accent.yellowBg, border: `1px solid ${energyStatus === "critical" || costStatus === "critical" ? t.accent.red : t.accent.yellow}44`, color: energyStatus === "critical" || costStatus === "critical" ? t.accent.red : t.accent.yellow, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 10 }}>
            {energyStatus === "critical" || costStatus === "critical" ? "🔴" : "⚠️"}
            {energyStatus !== "ok" && `${e.kwhThresholdAlert || "Günlük tüketim eşiği aşıldı"} (${todayKwh.toFixed(0)} kWh)`}
            {energyStatus !== "ok" && costStatus !== "ok" && " · "}
            {costStatus !== "ok"   && `${e.costThresholdAlert || "Günlük maliyet eşiği aşıldı"} (${money(todayCost)})`}
          </div>
        )}

        {/* Özet Metrikler */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
          {[
            { label: e.currentPower_   || "Anlık Güç",       value: summary?.current_power?.toFixed(1) ?? "—", unit: "kW",  icon: "⚡", color: statusColor(energyStatus, t), sub: `${e.threshold || "Eşik"}: ${thresholds.kwh_critical} kWh/${e.day || "gün"}` },
            { label: e.todayConsump_   || "Bugün Tüketim",   value: summary?.today_kwh?.toFixed(1)    ?? "—", unit: "kWh", icon: "📊", color: statusColor(energyStatus, t), sub: `${e.yesterday || "Dün"}: ${summary?.yesterday_kwh?.toFixed(1) ?? "—"} kWh` },
            { label: e.todayCost_      || "Bugün Maliyet",   value: summary?.today_cost?.toFixed(0)   ?? "—", unit: "₺",   icon: "💰", color: statusColor(costStatus, t),   sub: `${e.monthlyEst || "Aylık tahmini"}: ${money(summary?.monthly_est_cost)}` },
            { label: e.co2Emission     || "CO₂ Emisyonu",    value: summary?.today_co2?.toFixed(1)    ?? "—", unit: "kg",  icon: "🌿", color: t.accent.green,                sub: `${e.monthly_ || "Aylık"}: ${summary?.monthly_co2?.toFixed(0) ?? "—"} kg` },
          ].map(m => <MetricCard key={m.label} {...m} t={t} />)}
        </div>

        {/* Eşik Göstergeleri */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
          <ThresholdGauge label={e.dailyConsumption || "Günlük Tüketim"} value={todayKwh}                       unit="kWh" warning={thresholds.kwh_warning}             critical={thresholds.kwh_critical}              t={t} />
          <ThresholdGauge label={e.dailyCost        || "Günlük Maliyet"} value={todayCost}                      unit="₺"   warning={thresholds.cost_warning}            critical={thresholds.cost_critical}             t={t} />
          <ThresholdGauge label={e.co2Emission      || "CO₂ Emisyonu"}   value={summary?.today_co2 || 0}        unit="kg"  warning={thresholds.co2_warning  || 400}      critical={thresholds.co2_critical  || 600}      t={t} />
          <ThresholdGauge label={e.currentPower_    || "Anlık Güç"}      value={summary?.current_power || 0}   unit="kW"  warning={thresholds.peak_warning || 150}      critical={thresholds.peak_critical || 200}      t={t} />
        </div>

        {/* Ana Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 12, padding: 4, width: "fit-content" }}>
          {[
            ["overview",  `📈 ${e.tabOverview  || "Genel Bakış"}`],
            ["machines",  `🏭 ${e.tabMachines  || "Makine Bazlı"}`],
            ["analysis",  `🔍 ${e.tabAnalysis  || "Analiz"}`],
          ].map(([v, label]) => (
            <button key={v} onClick={() => setMainTab(v)} style={{ padding: "9px 18px", borderRadius: 9, border: "none", cursor: "pointer", background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none", color: mainTab === v ? t.text.primary : t.text.muted, fontSize: 13, fontWeight: 600, boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s" }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{e.loading || "Yükleniyor..."}</span>
          </div>
        ) : (
          <>
            {/* GENEL BAKIŞ */}
            {mainTab === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: t.text.muted }}>{e.show || "Göster"}:</span>
                  {[
                    ["hourly",  e.periodHourly  || "Saatlik"],
                    ["daily",   e.periodDaily   || "Günlük"],
                    ["monthly", e.periodMonthly || "Aylık"],
                  ].map(([v, label]) => (
                    <button key={v} onClick={() => setPeriod(v)} style={{ padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer", background: period === v ? t.accent.blue : t.bg.toggle, color: period === v ? "#fff" : t.text.muted, fontSize: 12, fontWeight: 600, transition: "all 0.2s" }}>{label}</button>
                  ))}
                </div>

                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 20 }}>
                    ⚡ {e.energyConsumption || "Enerji Tüketimi"} ({periodLabel})
                  </div>
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="kwhGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={t.accent.blue}  stopOpacity={0.3} />
                            <stop offset="95%" stopColor={t.accent.blue}  stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={t.accent.green} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={t.accent.green} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                        <XAxis dataKey={xKey} stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} tickFormatter={xFormatter} />
                        <YAxis yAxisId="left"  stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} />
                        <YAxis yAxisId="right" orientation="right" stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} />
                        <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                        <Legend />
                        <Area yAxisId="left"  type="monotone" dataKey="kwh"  stroke={t.accent.blue}  fill="url(#kwhGrad)"  strokeWidth={2} name="kWh" />
                        <Area yAxisId="right" type="monotone" dataKey="cost" stroke={t.accent.green} fill="url(#costGrad)" strokeWidth={2} name={`${e.cost_ || "Maliyet"} (₺)`} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                      {e.noEnergyData || "Enerji verisi bulunamadı"}
                    </div>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                  {[
                    { label: e.tariffDay   || "Gündüz Tarifesi", value: `${thresholds.tariff_day   || 2.5} ₺/kWh`, icon: "☀️", color: t.accent.yellow },
                    { label: e.tariffNight || "Gece Tarifesi",   value: `${thresholds.tariff_night || 1.8} ₺/kWh`, icon: "🌙", color: t.accent.blue   },
                    { label: e.tariffPeak  || "Puant Tarifesi",  value: `${thresholds.tariff_peak  || 3.2} ₺/kWh`, icon: "📈", color: t.accent.red    },
                  ].map(({ label, value, icon, color }) => (
                    <div key={label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 12, padding: "16px 20px", boxShadow: t.shadow.card, display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 24 }}>{icon}</span>
                      <div>
                        <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MAKİNE BAZLI */}
            {mainTab === "machines" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {machines.length === 0 ? (
                  <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>
                    {e.noMachineData || "Makine enerji verisi bulunamadı"}
                  </div>
                ) : (
                  <>
                    <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 20 }}>
                        🏭 {e.machineConsumptionToday || "Makine Bazlı Bugünkü Tüketim"}
                      </div>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={machines} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} horizontal={false} />
                          <XAxis type="number" stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} />
                          <YAxis type="category" dataKey="machine_name" stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} width={80} />
                          <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                          <Bar dataKey="today_kwh" fill={t.accent.blue} radius={[0, 4, 4, 0]} name="kWh" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: t.bg.theadRow }}>
                            {[e.machine_||"Makine", e.currentPower_||"Anlık Güç", e.todayKwh||"Bugün (kWh)", e.todayCostTl||"Bugün (₺)", e.monthKwh||"Bu Ay (kWh)", e.monthCostTl||"Bu Ay (₺)", e.efficiency||"Verimlilik", e.detail||"Detay"].map(h => (
                              <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {machines.map((m, i) => {
                            const eff = m.efficiency || 0;
                            const effColor = eff >= 90 ? t.accent.green : eff >= 70 ? t.accent.yellow : t.accent.red;
                            return (
                              <tr key={m.machine_id || i} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                                <td style={{ padding: "12px 14px", fontWeight: 700, color: t.text.primary, fontSize: 13 }}>{m.machine_name || m.machine_id}</td>
                                <td style={{ padding: "12px 14px", fontWeight: 700, color: t.accent.yellow, fontSize: 13 }}>{m.current_power?.toFixed(1) ?? "—"} kW</td>
                                <td style={{ padding: "12px 14px", color: t.accent.blue,   fontWeight: 600, fontSize: 13 }}>{m.today_kwh?.toFixed(1)   ?? "—"}</td>
                                <td style={{ padding: "12px 14px", color: t.accent.green,  fontWeight: 600, fontSize: 13 }}>{m.today_cost?.toFixed(0)  ?? "—"} ₺</td>
                                <td style={{ padding: "12px 14px", color: t.text.secondary, fontSize: 13 }}>{m.month_kwh?.toFixed(0)  ?? "—"}</td>
                                <td style={{ padding: "12px 14px", color: t.accent.purple, fontWeight: 600, fontSize: 13 }}>{m.month_cost?.toFixed(0) ?? "—"} ₺</td>
                                <td style={{ padding: "12px 14px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <div style={{ flex: 1, height: 6, background: t.bg.tag, borderRadius: 3, overflow: "hidden" }}>
                                      <div style={{ height: "100%", width: `${eff}%`, background: effColor, borderRadius: 3 }} />
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: effColor, flexShrink: 0 }}>{eff}%</span>
                                  </div>
                                </td>
                                <td style={{ padding: "12px 14px" }}>
                                  <button onClick={() => setSelectedMachine(m)} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, color: t.accent.blue }}>
                                    {e.detail || "Detay"}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ANALİZ */}
            {mainTab === "analysis" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {[
                    {
                      title: `📅 ${e.weeklyComparison || "Haftalık Karşılaştırma"}`,
                      data: [
                        { name: e.thisWeek || "Bu Hafta",   kwh: summary?.this_week_kwh  || 0, cost: summary?.this_week_cost  || 0 },
                        { name: e.lastWeek || "Geçen Hafta",kwh: summary?.last_week_kwh  || 0, cost: summary?.last_week_cost  || 0 },
                      ],
                    },
                    {
                      title: `📆 ${e.monthlyComparison || "Aylık Karşılaştırma"}`,
                      data: [
                        { name: e.thisMonth || "Bu Ay",    kwh: summary?.this_month_kwh || 0, cost: summary?.this_month_cost || 0 },
                        { name: e.lastMonth || "Geçen Ay", kwh: summary?.last_month_kwh || 0, cost: summary?.last_month_cost || 0 },
                      ],
                    },
                  ].map(({ title, data }) => (
                    <div key={title} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 20, boxShadow: t.shadow.card }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>{title}</div>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={data}>
                          <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                          <XAxis dataKey="name" stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} />
                          <YAxis stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} />
                          <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                          <Legend />
                          <Bar dataKey="kwh"  fill={t.accent.blue}  radius={[4,4,0,0]} name="kWh" />
                          <Bar dataKey="cost" fill={t.accent.green} radius={[4,4,0,0]} name="₺" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                  {[
                    { label: e.thisMonthTotal  || "Bu Ay Toplam",       value: summary?.this_month_kwh?.toFixed(0),   unit: "kWh", color: t.accent.blue,     icon: "⚡" },
                    { label: e.thisMonthCost   || "Bu Ay Maliyet",      value: summary?.this_month_cost?.toFixed(0),  unit: "₺",   color: t.accent.green,    icon: "💰" },
                    { label: e.thisMonthCo2    || "Bu Ay CO₂",          value: summary?.this_month_co2?.toFixed(0),   unit: "kg",  color: t.accent.yellow,   icon: "🌿" },
                    { label: e.lastMonth       || "Geçen Ay",           value: summary?.last_month_kwh?.toFixed(0),   unit: "kWh", color: t.text.secondary,  icon: "📅" },
                    { label: e.lastMonthCost   || "Geçen Ay Maliyet",   value: summary?.last_month_cost?.toFixed(0),  unit: "₺",   color: t.text.secondary,  icon: "💸" },
                    { label: e.yearlyEstimate  || "Yıllık Tahmini",     value: summary?.yearly_est_cost ? `${(summary.yearly_est_cost/1000).toFixed(0)}K` : "—", unit: "₺", color: t.accent.purple, icon: "📊" },
                  ].map(({ label, value, unit, color, icon }) => (
                    <div key={label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 12, padding: "16px 18px", boxShadow: t.shadow.card, display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 22 }}>{icon}</span>
                      <div>
                        <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 3, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color }}>
                          {value ?? "—"}<span style={{ fontSize: 11, color: t.text.muted, marginLeft: 3 }}>{unit}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showSettings && (
        <ThresholdSettingsModal settings={settings} t={t}
          onClose={() => setShowSettings(false)}
          onSaved={(newSettings) => { setSettings(newSettings); setShowSettings(false); }} />
      )}
      {selectedMachine && (
        <MachineEnergyModal machine={selectedMachine} t={t} onClose={() => setSelectedMachine(null)} />
      )}
    </div>
  );
}
